import { memo, useEffect } from 'react';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { shallowEqual, useDispatch } from 'react-redux';

import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { photoUtils } from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserPropertyPhotoWebApiHandler_upload } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import PropertiesActions from '../../actions/PropertiesActions';
import { useMultiplexBuildingSessionId, useMultiplexListingFlow } from '../../hooks/queries/multiplex';
import useMultiplexPhotoUpload from '../../hooks/queries/multiplex/useMultiplexPhotoUpload';
import { RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import { GET_LISTING_RECS } from '../../constants/RecommendationsConstants';
import Photo from '../../model/Photo';

import UploadDropzone from '../../components/UploadDropzone';

import { createZgApiHttpClient } from '../../../client/zgApiHttpClient';

import type { Dispatch, SetStateAction } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';
import type { DatarouterError, UserPropertyPhotoUploadResponseWebDto } from '@zg-rentals/rental-manager-env';
import type { UserPropertyMultiplexPhotoUploadResponseWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const uploadingCountMap: Record<string, number> = {};

type Props = {
  isSmallDropzone?: boolean;
  setUploadingCount: Dispatch<SetStateAction<number>>;
  uploadingCount: number;
};

const PhotoUpload = ({ isSmallDropzone = false, setUploadingCount, uploadingCount }: Props) => {
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const { photos, isSelfServeOnboardingInProgress, isUpdateRestricted, zgApiPublic } = useSelector((state) => {
    return {
      photos: state.properties.currentProperty.photos,
      isSelfServeOnboardingInProgress:
        state.properties.currentProperty.selfServeOnboardingStatus.isSelfServeOnboardingInProgress,
      isUpdateRestricted: state.properties.currentProperty.selfServeOnboardingStatus.isUpdateRestricted,
      zgApiPublic: state.env.zgApiPublic,
    };
  }, shallowEqual);

  // 3 minute timeout for photo uploads
  const { mutateAsync: uploadPhotoToDB } = useRentalManagerUserPropertyPhotoWebApiHandler_upload({
    httpClient: createZgApiHttpClient(zgApiPublic, { timeout: 3 * 60 * 1000 }),
  });

  const { mutateAsync: multiplexUploadPhotoAsync } = useMultiplexPhotoUpload({
    httpClient: createZgApiHttpClient(zgApiPublic, { timeout: 3 * 60 * 1000 }),
    meta: {
      invalidates: ['getMultiplexUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoUpload',
        details: 'PhotoUpload',
      },
    },
  });

  useEffect(() => {
    if (uploadingCountMap[listingId] && Number(uploadingCountMap[listingId] || 0) !== uploadingCount) {
      setUploadingCount(uploadingCountMap[listingId]);
    }

    if (!uploadingCountMap[listingId] && uploadingCount !== 0) {
      setUploadingCount(0);
    }
  }, [uploadingCountMap[listingId]]);

  const onDropAccepted = async (acceptedFiles: Array<Blob>) => {
    const promiseArr: Array<
      Promise<UserPropertyPhotoUploadResponseWebDto | UserPropertyMultiplexPhotoUploadResponseWebDto>
    > = [];
    let errorArr: Array<string | undefined> = [];

    const validatedAcceptedFiles = await photoUtils.filterForValidFiles(acceptedFiles, onDropRejected);

    uploadingCountMap[listingId] = validatedAcceptedFiles.length;
    setUploadingCount(validatedAcceptedFiles.length);

    return photoUtils.preloadImages(validatedAcceptedFiles).then((preloadedFiles) => {
      (preloadedFiles as Array<Blob>)?.forEach((file) => {
        if (isInMultiplexListingFlow) {
          promiseArr.push(
            multiplexUploadPhotoAsync({
              getParams: {
                multiplexBuildingSessionId: multiplexBuildingSessionId as string,
              },
              postData: { sourceFile: file },
            }),
          );
        } else {
          promiseArr.push(
            uploadPhotoToDB({
              getParams: { listingAlias: listingId },
              postData: { sourceFile: file },
            }),
          );
        }
      });

      if (promiseArr.length > 0) {
        return Promise.allSettled(promiseArr)
          .then((response) => {
            // filter out error objects from response array
            // appear when user uploads photos in offline / unstable network mode (user sees blank photo placeholder)
            const photoArray = (
              response as unknown as Array<{
                error: unknown;
                url?: string;
                status: 'fulfilled' | 'rejected';
                reason?: DatarouterError;
                value: PhotoProps;
              }>
            )
              .filter((responseObject) => {
                if (responseObject.status === 'rejected') {
                  errorArr.push(responseObject?.reason?.message || 'There was an error uploading your photo');
                  return false;
                }

                const value = responseObject.value as unknown;
                if (
                  value &&
                  typeof value === 'object' &&
                  'units' in value &&
                  Array.isArray((value as UserPropertyMultiplexPhotoUploadResponseWebDto).units)
                ) {
                  const mux = value as UserPropertyMultiplexPhotoUploadResponseWebDto;
                  if (!mux.success) {
                    errorArr.push(mux.errorMessage || 'There was an error uploading your photo');
                    return false;
                  }

                  const unitForListing = mux.units.find((u) => u.listingAlias === listingId);
                  if (!unitForListing?.success || !unitForListing.photo) {
                    errorArr.push(unitForListing?.errorMessage || 'There was an error uploading your photo');
                    return false;
                  }

                  return true;
                }

                const foundError = Boolean(responseObject.error && responseObject.url === undefined);

                if (foundError) {
                  errorArr.push(responseObject?.reason?.message || 'There was an error uploading your photo');
                }

                return !foundError;
              })
              .map((successfulPhoto) => {
                const value = successfulPhoto.value as unknown;
                if (
                  value &&
                  typeof value === 'object' &&
                  'units' in value &&
                  Array.isArray((value as UserPropertyMultiplexPhotoUploadResponseWebDto).units)
                ) {
                  const mux = value as UserPropertyMultiplexPhotoUploadResponseWebDto;
                  const unitForListing = mux.units.find((u) => u.listingAlias === listingId);
                  return Photo(unitForListing?.photo ?? {});
                }

                return Photo(value as UserPropertyPhotoUploadResponseWebDto);
              });

            dispatch(
              PropertiesActions.updateCurrentProperty({
                photos: [...photos, ...photoArray],
              }),
            );

            queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
            queryClient.invalidateQueries({ queryKey: ['getMultiplexUnits'] });
            queryClient.invalidateQueries({ queryKey: ['getGroupableProperties'] });

            if (errorArr.filter((err) => err !== undefined).length > 0) {
              throw new Error();
            }

            return photoArray;
          })
          .catch(() => {
            new Set(errorArr).forEach((err) => {
              logError({ error: err, context: 'uploadPhoto' });
              enqueueToast(<Toast tone="critical">{err} </Toast>);
            });
          })
          .finally(() => {
            delete uploadingCountMap[listingId];

            errorArr = [];
          });
      }

      return;
    });
  };

  const onDropRejected = (errorData?: Array<{ errors?: Array<{ message: string; code?: string }> }>) => {
    const error = errorData?.[0]?.errors?.[0];
    enqueueToast(
      <Toast tone="critical">
        {error?.code === ErrorCode.FileInvalidType ? 'File type must be an image' : error?.message}
      </Toast>,
    );
  };

  const isUploadDisabled = uploadingCount > 0 || (isSelfServeOnboardingInProgress && isUpdateRestricted);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, acceptedFiles } = useDropzone({
    accept: 'image/*',
    onDropAccepted,
    onDropRejected,
    disabled: isUploadDisabled,
  });

  const getRootPropsWithTabIndex = () => ({
    ...getRootProps({ isFocused, isDragAccept, isDragReject, acceptedFiles }),
    tabIndex: isSmallDropzone ? 0 : -1,
  });

  return (
    <UploadDropzone
      isSmallDropzone={isSmallDropzone}
      isDisabled={isUploadDisabled}
      isEmpty={photos.length === 0 && uploadingCount === 0}
      rootProps={getRootPropsWithTabIndex()}
      inputProps={getInputProps()}
    />
  );
};

export default memo(PhotoUpload);
